import React, { forwardRef, useLayoutEffect, useRef, useState } from 'react';
import QRCode from 'react-qr-code';
import { toPng } from 'html-to-image';

const fmt = (d) => new Date(d).toLocaleDateString('en-IN');

export const downloadCardPng = async (node, cardNumber) => {
  const dataUrl = await toPng(node, { quality: 1, pixelRatio: 2, cacheBust: true, skipFonts: true });
  const link = document.createElement('a');
  link.download = `GreenCard_${cardNumber}.png`;
  link.href = dataUrl;
  link.click();
};

export const printCardAtAadhaarSize = async (node, cardNumber) => {
  const dataUrl = await toPng(node, { quality: 1, pixelRatio: 4, cacheBust: true, skipFonts: true });
  const iframe = document.createElement('iframe');
  iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
  document.body.appendChild(iframe);
  const doc = iframe.contentWindow.document;
  doc.open();
  doc.write(`<!doctype html><html><head><title>GreenCard_${cardNumber}</title><style>
    @page { size: A4 portrait; margin: 15mm; }
    html, body { margin: 0; padding: 0; }
    .card { width: 85.6mm; height: 54mm; display: block; border-radius: 3.2mm; }
    .cut { display: inline-block; padding: 2mm; border: 0.2mm dashed #999; }
    p { font: 9pt sans-serif; color: #666; margin: 3mm 0 0; }
  </style></head><body><div class="cut"><img class="card" src="${dataUrl}" /></div>
  <p>Size: 85.6mm × 54mm (Aadhaar / ID-1). Print at 100% scale ("Actual size"), not "Fit to page".</p></body></html>`);
  doc.close();
  await new Promise(r => { const img = doc.querySelector('img'); if (img.complete) r(); else img.onload = r; });
  iframe.contentWindow.focus();
  iframe.contentWindow.print();
  setTimeout(() => iframe.remove(), 60000);
};

const Field = ({ label, children, className = '' }) => (
  <div>
    <div className="text-[10.5px] font-semibold text-gray-500 leading-tight">{label}</div>
    <div className={`text-[13px] font-bold text-gray-800 leading-tight ${className}`}>{children}</div>
  </div>
);

const CARD_W = 428;
const CARD_H = 270;

export const GreenCardVisual = forwardRef(({ card }, ref) => {
  const isExpired = new Date(card.valid_until) < new Date();
  const wrapRef = useRef(null);
  const [scale, setScale] = useState(1);
  useLayoutEffect(() => {
    const el = wrapRef.current?.parentElement;
    if (!el) return;
    const update = () => setScale(Math.min(1, el.clientWidth / CARD_W));
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return (
    <div ref={wrapRef} style={{ width: CARD_W * scale, height: CARD_H * scale }} className="shrink-0">
    <div style={{ transform: `scale(${scale})`, transformOrigin: 'top left', width: CARD_W, height: CARD_H }}>
    <div
      ref={ref}
      data-testid="greencard-id-card"
      className={`w-[428px] h-[270px] rounded-xl overflow-hidden shadow-2xl relative bg-white border-[3px] text-gujarati flex flex-col ${isExpired ? 'border-red-500' : 'border-green-600'}`}
      style={{ backgroundColor: '#ffffff', fontFamily: "'Noto Sans Gujarati', sans-serif" }}
    >
      <div className="bg-[#172554] h-[54px] shrink-0 px-3 flex items-center justify-between border-b-[3px] border-green-500">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center overflow-hidden p-0.5">
            <img src="/kagajbook-logo.png" alt="કાગજબુક" className="w-full h-full object-contain" data-testid="greencard-card-logo" />
          </div>
          <h2 className="text-white text-2xl font-bold leading-none" data-testid="greencard-card-brand">કાગજબુક</h2>
        </div>
        <div className="bg-green-500 text-[#172554] text-[11px] font-bold px-2.5 py-0.5 rounded-full whitespace-nowrap shrink-0 mr-1" data-testid="greencard-card-badge">ગ્રીન કાર્ડ</div>
      </div>

      <div className="flex-1 px-3 pt-2 relative flex gap-3 min-h-0">
        <div className="absolute inset-0 opacity-5 pointer-events-none" style={{ backgroundImage: 'radial-gradient(#16a34a 1px, transparent 1px)', backgroundSize: '14px 14px' }}></div>
        <div className="flex-1 space-y-1.5 relative z-10 min-w-0">
          <Field label="પૂરું નામ" className="!text-[16px] uppercase truncate"><span data-testid="greencard-card-name">{card.full_name}</span></Field>
          <div className="grid grid-cols-2 gap-2">
            <Field label="જન્મ તારીખ">{fmt(card.dob)}</Field>
            <Field label="કુટુંબ">{card.family_members} સભ્યો</Field>
          </div>
          <Field label="સ્થળ અને સંપર્ક" className="truncate text-wrap break-words">{card.city}, {card.state}{card.pincode ? ` - ${card.pincode}` : ''} | +91 {card.user_phone}</Field>
          {card.address && (
            <div>
              <div className="text-[10.5px] font-semibold text-gray-500 leading-tight">સરનામું</div>
              <div className="text-[11px] font-semibold text-gray-800 leading-snug line-clamp-2 break-words" data-testid="greencard-card-address">{card.address}</div>
            </div>
          )}
        </div>
        <div className="relative z-10 flex flex-col items-end shrink-0 w-[96px]">
          <div className="p-1 bg-white border border-gray-200 rounded-md">
            <QRCode value={`KAGAJBOOK-GREEN-${card.card_number}-${card.user_phone}`} size={70} level="H" />
          </div>
          <div className="mt-1.5 text-right">
            <div className="text-[10px] font-semibold text-gray-500 leading-tight">કાર્ડ નંબર</div>
            <div className="text-[13px] font-black text-[#172554] tracking-wider leading-tight whitespace-nowrap" data-testid="greencard-card-number">{card.card_number}</div>
          </div>
        </div>
      </div>

      <div className="mx-3 mb-2.5 pt-1 border-t border-dashed border-gray-300 flex justify-between items-end gap-6 shrink-0 relative z-10 whitespace-nowrap">
        <div className="text-left">
          <div className="text-[10px] font-semibold text-gray-500 leading-tight">સભ્યપદ શરૂ તારીખ</div>
          <div className="text-[13px] font-bold text-green-700 leading-tight">{fmt(card.valid_from)}</div>
        </div>
        <div className="text-right">
          <div className="text-[10px] font-semibold text-gray-500 leading-tight">નવીકરણ તારીખ</div>
          <div className={`text-[13px] font-bold leading-tight ${isExpired ? 'text-red-600' : 'text-green-700'}`}>{fmt(card.valid_until)}</div>
        </div>
      </div>
      <div className="bg-green-600 h-1.5 w-full absolute bottom-0"></div>
    </div>
    </div>
    </div>
  );
});
